const toArray = (value) => (Array.isArray(value) ? value : []);

/**
 * AI may return scores as 0–1 (0.75) or 0–100 (80). UI expects 0–100 integers.
 * Values in (0, 1] are treated as fractions (0.75→75, 1→100).
 */
export const normalizeAiPercentScore = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  const asPercent = n <= 1 ? n * 100 : n;
  return Math.max(0, Math.min(100, Math.round(asPercent)));
};

const getUnitAccommodationId = (unit) =>
  unit?.accomodation_id ?? unit?.accommodation_id ?? unit?.accommodation?.id ?? null;

const getUnitBlockId = (unit) => unit?.block_id ?? unit?.block?.id ?? null;

const getUnitAccommodationType = (unit) =>
  unit?.accommodation?.accomodationType ?? unit?.accommodation?.accommodationType ?? null;

/** GROUP accommodation id linked to a block via its units. */
export const findGroupAccommodationIdForBlock = (units, blockId) => {
  if (!units?.length || blockId == null) return null;
  const targetBlockId = Number(blockId);
  const match = units.find((unit) => {
    const unitBlockId = getUnitBlockId(unit);
    if (unitBlockId == null) return false;
    const accType = String(getUnitAccommodationType(unit) || '').toUpperCase();
    return Number(unitBlockId) === targetBlockId && accType === 'GROUP';
  });
  const accommodationId = getUnitAccommodationId(match);
  return accommodationId != null ? Number(accommodationId) : null;
};

/** Units linked to a BOQ/BOM accommodation (independent picker or group sync). */
export const mapIndependentUnitsForAccommodation = (units, accommodationId) => {
  if (!units?.length || accommodationId == null) return [];
  const targetId = Number(accommodationId);
  return units
    .filter((unit) => {
      const unitAccommodationId = getUnitAccommodationId(unit);
      if (unitAccommodationId == null || unitAccommodationId === '') return false;
      return Number(unitAccommodationId) === targetId;
    })
    .map((unit) => ({
      id: unit.id,
      label: unit.unitName || `Unit ${unit.id}`
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/** First unit id linked to an accommodation — used for group BOM sync (`GroupUnit`). */
export const findUnitIdForAccommodationSync = (units, accommodationId) =>
  mapIndependentUnitsForAccommodation(units, accommodationId)[0]?.id ?? null;

/** Units available for independent BOM sync — excludes units already synced. */
export const mapBomSyncStatusUnits = (response) => {
  const root = response?.data ?? response ?? {};
  return toArray(root.units)
    .filter((unit) => !unit.isSynced)
    .map((unit) => ({
      id: unit.unitId,
      label: unit.unitName || `Unit ${unit.unitId}`
    }))
    .filter((unit) => unit.id != null)
    .sort((a, b) => a.label.localeCompare(b.label));
};

const uniqueSources = (roomSpaceData, workTypeExtraction) => {
  const sources = new Set();
  roomSpaceData.forEach((room) => {
    if (room?.source_drawing) sources.add(room.source_drawing);
  });
  workTypeExtraction.forEach((workType) => {
    toArray(workType?.extracted_parameters).forEach((param) => {
      if (param?.source) sources.add(param.source);
    });
  });
  return [...sources];
};

export const mapAiFoundFiles = (roomSpaceData, workTypeExtraction) =>
  uniqueSources(roomSpaceData, workTypeExtraction).map((fileName, index) => ({
    id: index + 1,
    category: 'Processed drawing',
    fileName
  }));

export const mapMissingDataItems = (missingData) => {
  const roomMissing = toArray(missingData?.roomLayerMissing);
  const worktypeMissing = toArray(missingData?.worktypeLayerMissing);

  return [
    ...roomMissing.map((label, index) => ({
      id: `room-${index}`,
      label,
      layer: 'room',
      accuracyBoost: 'Room layer',
      questionId: null
    })),
    ...worktypeMissing.map((label, index) => ({
      id: `worktype-${index}`,
      label,
      layer: 'worktype',
      accuracyBoost: 'Work type',
      questionId: null
    }))
  ];
};

export const mapExtractedParameterRows = (workTypeExtraction) =>
  workTypeExtraction.flatMap((workType) =>
    toArray(workType?.extracted_parameters).map((param, index) => ({
      id: `${workType.workTypeId}-${param.name}-${index}`,
      workType: `${workType.workTypeName} — ${param.name}`,
      quantity: String(param.value ?? '—'),
      unit: param.unit ?? '—',
      source: param.source ?? '—',
      confidence: param.confidence ?? 'Medium',
      workTypeId: workType.workTypeId
    }))
  );

export const mapRoomSpaceRows = (roomSpaceData) =>
  roomSpaceData.map((room, index) => ({
    id: index + 1,
    roomName: room.room_name ?? '—',
    area: room.area != null ? String(room.area) : '—',
    dimensions:
      room.width && room.length ? `${room.width} × ${room.length}` : '—',
    perimeter: room.perimeter != null ? String(room.perimeter) : '—',
    usageType: room.usage_type ?? '—',
    floor: room.floor_association ?? '—',
    confidence: room.confidence ?? 'Medium',
    source: room.source_drawing ?? '—'
  }));

export const mapClarificationQuestionItem = (q) => ({
  id: String(q.id),
  label: q.question,
  answer: q.answer ?? null,
  options: toArray(q.options).map((opt) => ({
    id: opt.id,
    label: opt.label,
    value: opt.value
  })),
  allowsCustomInput: Boolean(q.allowsCustomInput),
  reason: q.reason,
  raw: q
});

export const mapClarificationQuestionsToSections = (clarificationQuestions) => {
  const questions = toArray(clarificationQuestions);
  if (!questions.length) return [];

  return [
    {
      id: 'clarification',
      title: 'Clarification questions',
      questions: questions.map(mapClarificationQuestionItem)
    }
  ];
};

/** Maps PUT `/boq/option` or `/boq/question` response `data` to a question item. */
export const mapBoqQuestionUpdateResponse = (response) => {
  const q = response?.data ?? response ?? {};
  return mapClarificationQuestionItem(q);
};

export const mapPresumedValuesFromExtraction = (workTypeExtraction) =>
  workTypeExtraction.flatMap((workType, wtIndex) =>
    toArray(workType?.extracted_parameters).map((param, index) => ({
      id: `${workType.workTypeId}-${index}`,
      label: `${workType.workTypeName} — ${param.name}`,
      value: `${param.value ?? '—'} ${param.unit ?? ''}`.trim()
    }))
  );

export const countQuestionsInSections = (sections) =>
  sections.reduce((sum, section) => sum + toArray(section.questions).length, 0);

/** Server truth: question row has non-empty `answer`. */
export const countAnsweredClarificationQuestions = (clarificationQuestions) =>
  toArray(clarificationQuestions).filter((q) => {
    const answer = q.answer;
    return answer != null && String(answer).trim() !== '';
  }).length;

const resolveBoqQuestionsRoot = (response) => response?.data ?? response ?? {};

const resolveClarificationQuestions = (response) => {
  const root = resolveBoqQuestionsRoot(response);
  if (Array.isArray(root)) return root;
  return toArray(root.clarificationQuestions ?? root.questions);
};

/** Normalizes `GET /boq/questions` response for the questions page. */
export const mapBoqQuestionsResponse = (response) => {
  const root = resolveBoqQuestionsRoot(response);
  const clarificationQuestions = resolveClarificationQuestions(response);
  const questionSections = mapClarificationQuestionsToSections(clarificationQuestions);
  const totalQuestions = countQuestionsInSections(questionSections);
  const answeredCount = countAnsweredClarificationQuestions(clarificationQuestions);
  const nextStep = root.nextStep ?? {};

  return {
    clarificationQuestions,
    questionSections,
    totalQuestions,
    answeredCount,
    aiValueCount: clarificationQuestions.length,
    nextStep,
    readyForBoqEngine: Boolean(nextStep.readyForBoqEngine),
    notesForNextStep:
      nextStep.notesForNextStep ??
      'Answer all clarification questions to enable BOQ generation.',
    accommodationId: root.accommodation_id ?? root.accommodationId ?? null,
    projectId: root.project_id ?? root.projectId ?? null,
    scope: root.scope ?? null
  };
};

const mapBoqItemRow = (item, sectionId, index) => ({
  id: item.id ?? `${sectionId}-${index}`,
  itemName: item.item_name ?? '—',
  description: item.description ?? '',
  quantity:
    item.quantity === 'NOT COMPUTABLE' || item.quantity == null
      ? '—'
      : String(item.quantity),
  unit: item.unit ?? '—',
  formula: item.calculation_formula ?? '',
  source: item.source_data ?? '—',
  confidence: item.confidence ?? 'Medium',
  isComputable:
    item.is_computable !== false &&
    item.quantity !== 'NOT COMPUTABLE' &&
    item.quantity != null
});

export const formatBomQuantity = (value) => {
  if (value == null) return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return Number.isInteger(num) ? String(num) : num.toFixed(2).replace(/\.0+$/, '');
};

const mapBomCombination = (combination, itemId, index) => ({
  id: `${itemId}-${index}`,
  combinationName: combination.combinationName ?? `Combination ${index + 1}`,
  title: combination.combinationName ?? `Combination ${index + 1}`,
  description: combination.suitabilityNotes ?? '',
  type: combination.type ?? '',
  materials: toArray(combination.materials).map((material) => ({
    id: material.materialId ?? material.materialName,
    name: material.materialName ?? '—',
    detail: [material.specification, material.consumptionLogic].filter(Boolean).join(' · '),
    qty: formatBomQuantity(material.finalQuantity ?? material.baseQuantity),
    unit: material.unit ?? '—',
    source: material.source ?? ''
  }))
});

/** Normalizes `POST /boq/bom/generate` response for the BOM tab. */
export const mapBomGenerateResponse = (response) => {
  const root = response?.data ?? response ?? {};
  const bomByBoqItem = toArray(root.bomByBoqItem);
  const inventoryUsage = root.inventoryUsageAnalysis ?? {};
  const validationSummary = root.validationSummary ?? {};

  const workTypeMap = new Map();
  bomByBoqItem.forEach((item) => {
    const workTypeId = item.workTypeId ?? item.workType;
    if (!workTypeMap.has(workTypeId)) {
      workTypeMap.set(workTypeId, {
        id: workTypeId,
        title: item.workType ?? 'Work type',
        boqItems: []
      });
    }
    workTypeMap.get(workTypeId).boqItems.push({
      id: item.boqItemId,
      description: item.itemDescription ?? '',
      quantityRef: item.quantityReference
        ? `${item.quantityReference.quantity} ${item.quantityReference.unit}`
        : '—',
      combinations: toArray(item.combinations).map((c, i) =>
        mapBomCombination(c, item.boqItemId, i)
      )
    });
  });

  const workSections = Array.from(workTypeMap.values()).map((section, index) => ({
    ...section,
    index: index + 1
  }));

  const materialCount = bomByBoqItem.reduce(
    (sum, item) =>
      sum +
      toArray(item.combinations).reduce(
        (s, combo) => s + toArray(combo.materials).length,
        0
      ),
    0
  );

  return {
    boqId: root.boqId,
    accommodationId: root.accommodationId,
    combinationCount: root.combinationCount,
    bomByBoqItem,
    workSections,
    materialCount,
    aiAssumedCount: inventoryUsage.externalMaterialsIntroducedCount ?? 0,
    inventoryMaterialsUsedCount: inventoryUsage.inventoryMaterialsUsedCount ?? 0,
    validationSummary,
    consistencyScore: normalizeAiPercentScore(validationSummary.consistencyScore)
  };
};

export const buildInitialBomSelections = (workSections) => {
  const selections = {};
  (workSections ?? []).forEach((section) => {
    (section.boqItems ?? []).forEach((item) => {
      selections[item.id] = item.combinations?.[0]?.id ?? null;
    });
  });
  return selections;
};

/** Builds `selections` array for `POST /boq/bom/applyCosting` from UI radio state. */
export const buildBomCostingSelections = (workSections, selectedByBoqItem) => {
  const selections = [];
  (workSections ?? []).forEach((section) => {
    (section.boqItems ?? []).forEach((item) => {
      const comboId = selectedByBoqItem[item.id];
      const combo =
        item.combinations?.find((c) => c.id === comboId) ?? item.combinations?.[0];
      if (!combo) return;
      selections.push({
        boq_item_id: item.id,
        combination_name: combo.combinationName ?? combo.title
      });
    });
  });
  return selections;
};

/** Primary combination name for `POST /boq/bom/sync` (project common). */
export const getBomSyncCombinationName = ({
  costingData,
  workSections,
  bomSelections,
  fallback = 'Standard'
} = {}) => {
  const fromCosting = costingData?.workSections
    ?.flatMap((section) => section.boqItems ?? [])
    .find((item) => item.selectedCombinationName)?.selectedCombinationName;
  if (fromCosting) return fromCosting;

  const fromSelections = buildBomCostingSelections(workSections, bomSelections);
  if (fromSelections[0]?.combination_name) return fromSelections[0].combination_name;

  const fromSaved = costingData?.savedMaterials?.[0]?.combinationName;
  if (fromSaved) return fromSaved;

  return fallback;
};

export const parseBomNumber = (value) => {
  if (value == null || value === '') return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

/** One persisted BOM material row (`savedMaterials[]` or GET /bom). */
export const mapSavedMaterialRow = (row) => ({
  rowId: row.id,
  boqItemId: row.boq_item_id,
  boqWorktypeId: row.boq_worktype_id,
  workTypeName: row.workTypeName ?? '',
  combinationName: row.combinationName ?? '',
  name: row.material ?? '—',
  inventoryId: row.inventory_id ?? null,
  quantity: parseBomNumber(row.quantity),
  unit: row.unit ?? '—',
  source: row.source ?? '',
  unitRate: parseBomNumber(row.rate),
  amount: parseBomNumber(row.amount)
});

export const computeBomMaterialAmount = (quantity, rate) => {
  const q = parseBomNumber(quantity);
  const r = parseBomNumber(rate);
  if (q == null || r == null) return null;
  return Math.round(q * r * 100) / 100;
};

/** PATCH body with only keys that changed vs the original row. */
export const buildBomMaterialPatch = (original, edited) => {
  const patch = {};
  const fields = [
    { key: 'inventoryId', api: 'inventory_id', numeric: true },
    { key: 'quantity', api: 'quantity', numeric: true },
    { key: 'source', api: 'source' },
    { key: 'unitRate', api: 'rate', numeric: true },
    { key: 'amount', api: 'amount', numeric: true },
    { key: 'combinationName', api: 'combinationName' }
  ];

  fields.forEach(({ key, api, numeric }) => {
    const origVal = original[key];
    const editVal = edited[key];
    const same = numeric
      ? parseBomNumber(origVal) === parseBomNumber(editVal)
      : String(origVal ?? '') === String(editVal ?? '');
    if (!same) {
      patch[api] = numeric ? parseBomNumber(editVal) : editVal;
    }
  });

  return patch;
};

const sumBoqItemGrandTotals = (workSections) =>
  (workSections ?? []).reduce(
    (sum, section) =>
      sum +
      (section.boqItems ?? []).reduce(
        (itemSum, item) => itemSum + (item.totalCost?.grandTotal ?? 0),
        0
      ),
    0
  );

const buildWorkSectionsFromSavedMaterials = (savedMaterials, pricedBoqItems) => {
  const pricedByBoqItem = new Map();
  pricedBoqItems.forEach((item) => pricedByBoqItem.set(item.boqItemId, item));

  const sectionMap = new Map();
  savedMaterials.forEach((raw) => {
    const row = mapSavedMaterialRow(raw);
    const sectionKey = row.boqWorktypeId ?? row.workTypeName;
    if (!sectionMap.has(sectionKey)) {
      sectionMap.set(sectionKey, {
        id: row.boqWorktypeId ?? sectionKey,
        title: row.workTypeName || 'Work type',
        boqItemsMap: new Map()
      });
    }
    const section = sectionMap.get(sectionKey);
    if (!section.boqItemsMap.has(row.boqItemId)) {
      const priced = pricedByBoqItem.get(row.boqItemId);
      section.boqItemsMap.set(row.boqItemId, {
        id: row.boqItemId,
        description: priced?.itemDescription ?? '',
        selectedCombinationName: priced?.selectedCombinationName ?? row.combinationName,
        materials: [],
        labourCosts: toArray(priced?.labourCosts),
        totalCost: priced?.totalCost ?? {}
      });
    }
    section.boqItemsMap.get(row.boqItemId).materials.push({
      ...row,
      detail: row.source ? `Source: ${row.source}` : ''
    });
  });

  return Array.from(sectionMap.values()).map((section, index) => {
    const boqItems = Array.from(section.boqItemsMap.values()).map((item) => {
      const materialTotal = item.materials.reduce((s, m) => s + (m.amount ?? 0), 0);
      const labourTotal = item.labourCosts.reduce((s, l) => s + (l.amount ?? 0), 0);
      return {
        ...item,
        totalCost: {
          materialTotal,
          labourTotal,
          grandTotal: materialTotal + labourTotal
        }
      };
    });
    return { id: section.id, title: section.title, index: index + 1, boqItems };
  });
};

/** Rebuilds UI sections after a successful material PUT. */
export const mergeMaterialUpdateIntoCostingData = (costingData, rowId, edited) => {
  const savedMaterials = toArray(costingData.savedMaterials).map((raw) => {
    if (raw.id !== rowId) return raw;
    return {
      ...raw,
      material: edited.name ?? raw.material,
      inventory_id: edited.inventoryId ?? raw.inventory_id,
      quantity:
        edited.quantity != null ? String(edited.quantity) : raw.quantity,
      unit: edited.unit ?? raw.unit,
      source: edited.source ?? raw.source,
      rate: edited.unitRate != null ? edited.unitRate : raw.rate,
      amount: edited.amount != null ? edited.amount : raw.amount,
      combinationName: edited.combinationName ?? raw.combinationName
    };
  });

  const pricedBoqItems = toArray(costingData.pricedBoqItems);
  const workSections = buildWorkSectionsFromSavedMaterials(savedMaterials, pricedBoqItems);
  const totalProjectCost = sumBoqItemGrandTotals(workSections);

  return {
    ...costingData,
    savedMaterials,
    workSections,
    materialCount: savedMaterials.length,
    totalProjectCost,
    summary: {
      ...costingData.summary,
      totalProjectCost
    }
  };
};

/** Unwrap GET /boq and GET /boq/bom envelopes (`data` object or single-item `data[]`). */
export const unwrapBoqApiRecord = (response) => {
  if (response == null) return null;

  let record = response.data !== undefined ? response.data : response;

  if (Array.isArray(record)) {
    record = record.length > 0 ? record[0] : null;
  }

  return record && typeof record === 'object' ? record : null;
};

/** Pick saved BOM from GET /boq/bom — when boqId is set, match that BOQ only. */
export const unwrapSavedBomRecord = (response, boqId = null) => {
  if (response == null) return null;

  let records = response.data !== undefined ? response.data : response;

  if (!Array.isArray(records)) {
    return records && typeof records === 'object' ? records : null;
  }

  if (records.length === 0) return null;

  if (boqId != null) {
    const targetId = Number(boqId);
    const match = records.find((record) => {
      const recordBoqId = record?.boq_id ?? record?.boqId;
      return recordBoqId != null && Number(recordBoqId) === targetId;
    });
    return match ?? null;
  }

  return records[0];
};

/** Materials list from GET saved BOM or applyCosting (`materials` vs `savedMaterials`). */
const getSavedBomMaterialsFromRoot = (root) => {
  const materials = toArray(root?.materials);
  if (materials.length > 0) return materials;
  return toArray(root?.savedMaterials);
};

/** True when saved BOM payload includes priced materials (ready for sync). */
export const isSavedBomReady = (response, boqId = null) => {
  const record = unwrapSavedBomRecord(response, boqId);
  if (!record) return false;
  return getSavedBomMaterialsFromRoot(record).length > 0;
};

/** Normalizes `GET /boq/bom?accommodation_id=` saved BOM for the costing view. */
export const mapSavedBomResponse = (response, existingCostingData = null, boqId = null) => {
  const effectiveBoqId = boqId ?? existingCostingData?.boqId ?? null;
  const root = unwrapSavedBomRecord(response, effectiveBoqId) ?? {};
  const savedMaterials = getSavedBomMaterialsFromRoot(root);
  const pricedBoqItems = toArray(existingCostingData?.pricedBoqItems);
  const workSections = buildWorkSectionsFromSavedMaterials(savedMaterials, pricedBoqItems);
  const totalProjectCost = sumBoqItemGrandTotals(workSections);

  return {
    bomId: root.id ?? existingCostingData?.bomId,
    boqId: root.boq_id ?? existingCostingData?.boqId,
    accommodationId: root.accommodation_id ?? existingCostingData?.accommodationId,
    pricedBoqItems,
    workSections,
    savedMaterials,
    locationCostProfile: existingCostingData?.locationCostProfile ?? {},
    summary: {
      ...(existingCostingData?.summary ?? {}),
      totalProjectCost
    },
    totalProjectCost,
    materialCount: savedMaterials.length
  };
};

/** Normalizes `POST /boq/bom/applyCosting` response for the priced BOM / inventory view. */
export const mapBomApplyCostingResponse = (response) => {
  const root = response?.data ?? response ?? {};
  const pricedBoqItems = toArray(root.pricedBoqItems);
  const savedMaterials = toArray(root.savedMaterials);
  const workSections = buildWorkSectionsFromSavedMaterials(savedMaterials, pricedBoqItems);
  const totalProjectCost =
    root.summary?.totalProjectCost ?? sumBoqItemGrandTotals(workSections);

  return {
    bomId: root.bomId,
    boqId: root.boqId,
    accommodationId: root.accommodationId,
    pricedBoqItems,
    workSections,
    savedMaterials,
    locationCostProfile: root.locationCostProfile ?? {},
    summary: root.summary ?? {},
    totalProjectCost,
    materialCount: savedMaterials.length
  };
};

export const formatBoqBomCurrency = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const hasBoqExtractPayload = (root) =>
  Boolean(
    root?.roomSpaceData?.length ||
      root?.workTypeExtraction?.length ||
      root?.clarificationQuestions?.length ||
      root?.summaryIntelligence ||
      root?.missingData
  );

/** Normalizes `GET /boq?accommodation_id=` (optional `type=GroupCommon`) for restoring the detail page. */
export const mapGetBoqResponse = (response) => {
  const record = unwrapBoqApiRecord(response);
  if (!record) return {};

  const metadata = record.metadata ?? {};
  const restored = {};
  restored.boqId = record.id ?? record.boqId ?? null;

  if (hasBoqExtractPayload(record)) {
    restored.analysisData = mapBoqExtractResponse({ data: record });
    restored.analysisComplete = true;
  }

  const hasBoq =
    metadata.boq_output?.length ||
    record.boqWorktypes?.length ||
    metadata.final_summary;

  if (hasBoq) {
    restored.generatedBoqData = mapBoqGenerateResponse({ data: record });
    restored.boqGenerated = true;
    restored.analysisComplete = true;
  }

  const bomByBoqItem = metadata.bomByBoqItem ?? record.bomByBoqItem;
  if (bomByBoqItem?.length) {
    restored.generatedBomData = mapBomGenerateResponse({
      data: {
        bomByBoqItem,
        boqId: record.id ?? record.boqId,
        accommodationId: record.accommodation_id ?? record.accommodationId,
        combinationCount: metadata.combinationCount ?? record.combinationCount
      }
    });
    restored.bomGenerated = true;
    restored.analysisComplete = true;
  }

  const savedMaterials = metadata.materials ?? record.materials;
  if (savedMaterials?.length || record.pricedBoqItems?.length) {
    restored.costingData = mapSavedBomResponse({ data: record });
    restored.bomCostingApplied = true;
    restored.bomGenerated = true;
  }

  return restored;
};

/** True when `GET /boq/project-common/boq` has generated BOQ data. */
export const hasProjectCommonBoq = (response) => Boolean(mapGetBoqResponse(response)?.boqGenerated);

/** Extract BOQ id from `GET /boq/project-common/boq` response. */
export const getProjectCommonBoqId = (response) =>
  mapGetBoqResponse(response)?.generatedBoqData?.boqId ?? null;

const formatPresenceArea = (value, unit = 'sq/ft') => {
  if (value === null || value === undefined || value === '') return '—';
  return `${value} ${unit}`;
};

/** Map `GET /boq/check/presence` accommodation entry to listing card shape. */
export const mapPresenceAccommodationToCard = (item, measurementUnit = 'sq/ft') => {
  const details = item?.details ?? {};
  const unit = details.measurementUnit || measurementUnit;

  return {
    id: item?.accommodationId ?? null,
    name: item?.name || 'Unnamed',
    area: formatPresenceArea(details.plotArea ?? item?.plotArea, unit),
    buildUp: formatPresenceArea(details.buildUpArea ?? item?.buildUpArea, unit),
    carpet: formatPresenceArea(details.carpetArea ?? item?.carpetArea, unit),
    accommodationType: item?.accomodationType ?? null,
    hasBoq: Boolean(item?.presence?.boq?.present),
    hasBom: Boolean(item?.presence?.bom?.present),
    boqId: item?.presence?.boq?.boqId ?? null
  };
};

/** Map `GET /boq/check/presence` group block entry; optional block meta for area/levels. */
export const mapPresenceGroupBlockToCard = (item, blockMeta, measurementUnit = 'sq/ft') => {
  const details = item?.details ?? {};

  return {
    id: item?.blockId ?? null,
    name: item?.blockName || blockMeta?.name || 'Unnamed',
    area: formatPresenceArea(details.blockArea ?? blockMeta?.area, measurementUnit),
    levels:
      details.numberOfLevels ??
      blockMeta?.levels?.length ??
      blockMeta?.number_of_levels ??
      '—',
    accommodationId: item?.accommodationId ?? null,
    hasBoq: Boolean(item?.groupCommon?.boq?.present),
    hasBom: Boolean(item?.groupCommon?.bom?.present),
    boqId: item?.groupCommon?.boq?.boqId ?? null
  };
};

/** Map `GET /boq/check/presence` projectCommon to listing/navigation state. */
export const mapProjectCommonFromPresence = (projectCommon) => {
  if (!projectCommon) {
    return { hasBoq: false, hasBom: false, boqId: null };
  }
  return {
    hasBoq: Boolean(projectCommon.presence?.boq?.present),
    hasBom: Boolean(projectCommon.presence?.bom?.present),
    boqId: projectCommon.presence?.boq?.boqId ?? null
  };
};

/** Normalizes `POST /boq/generate` and saved `GET /boq` records for the BOQ tab. */
export const mapBoqGenerateResponse = (response) => {
  const root = response?.data ?? response ?? {};
  const metadata = root.metadata ?? root.boq?.metadata ?? {};
  const items = toArray(root.items);
  const boqGeneration = root.boqGeneration ?? metadata;
  const boqOutput = toArray(boqGeneration.boq_output ?? metadata.boq_output);
  const finalSummary = boqGeneration.final_summary ?? metadata.final_summary ?? {};
  const boqWorktypes = toArray(root.boqWorktypes);
  const sourceWorkTypes = items.length
    ? items
    : boqWorktypes.length
      ? boqWorktypes
      : boqOutput.map((wt) => ({
          workType_id: wt.work_type_id ?? wt.workType_id,
          workTypeName: wt.workTypeName ?? wt.work_type,
          boqWorktypeItems: wt.boq_items
        }));

  const workTypeSections = sourceWorkTypes.map((wt, index) => {
    const workItems = toArray(wt.boqWorktypeItems ?? wt.boq_items);
    const sectionId = wt.workType_id ?? wt.work_type_id ?? index;
    return {
      id: sectionId,
      index: index + 1,
      title: wt.workTypeName ?? wt.work_type ?? 'Work type',
      items: workItems.map((item, i) => mapBoqItemRow(item, sectionId, i))
    };
  });

  const boqRows = workTypeSections.flatMap((section) =>
    section.items.map((item) => ({
      id: item.id,
      workType: `${section.title} — ${item.itemName}`,
      quantity: item.quantity,
      unit: item.unit,
      source: item.source,
      confidence: item.confidence
    }))
  );

  return {
    workTypeSections,
    boqRows,
    finalSummary,
    totalBoqItems: finalSummary.total_boq_items ?? boqRows.length,
    overallConfidence: normalizeAiPercentScore(finalSummary.overall_confidence_score),
    readinessStatus: finalSummary.boq_readiness_status ?? null,
    boqId: root.boq?.id ?? root.id ?? null
  };
};

/** Normalizes `/boq/extractData` response for analysis & questions UI. */
export const mapBoqExtractResponse = (response) => {
  const root = response?.data ?? response ?? {};
  const roomSpaceData = toArray(root.roomSpaceData);
  const workTypeExtraction = toArray(root.workTypeExtraction);
  const missingDataRaw = root.missingData ?? {};
  const clarificationQuestions = toArray(root.clarificationQuestions);
  const summaryIntelligence = root.summaryIntelligence ?? {};
  const nextStep = root.nextStep ?? {};

  const questionSections = mapClarificationQuestionsToSections(clarificationQuestions);
  const totalQuestions = countQuestionsInSections(questionSections);
  const presumedValues = mapPresumedValuesFromExtraction(workTypeExtraction);

  const accuracy = normalizeAiPercentScore(
    summaryIntelligence.extractionCompletenessScore ??
      summaryIntelligence.roomCoverageScore ??
      0,
  );

  return {
    aiFoundFiles: mapAiFoundFiles(roomSpaceData, workTypeExtraction),
    missingData: mapMissingDataItems(missingDataRaw),
    boqRows: mapExtractedParameterRows(workTypeExtraction),
    roomSpaceRows: mapRoomSpaceRows(roomSpaceData),
    clarificationQuestions,
    questionSections,
    presumedValues,
    presumedCount: presumedValues.length,
    totalQuestions,
    summaryIntelligence,
    nextStep,
    accuracy,
    aiValueCount: clarificationQuestions.length,
    filesProcessed: summaryIntelligence.totalFilesProcessed ?? 0,
    readyForBoqEngine: Boolean(nextStep.readyForBoqEngine),
    footerNote:
      nextStep.notesForNextStep ??
      'Answer clarification questions to improve BOQ accuracy.'
  };
};

export const getApiErrorMessage = (err, fallback = 'Something went wrong') =>
  err?.response?.data?.message || err?.message || fallback;

/** True when POST /boq/bom/generate failed because BOQ must be (re)generated first. */
export const isBoqRegenerationRequiredForBomError = (err) => {
  const message = getApiErrorMessage(err, '').toLowerCase();
  if (!message) return false;

  return (
    message.includes('no boq line items found') ||
    message.includes('no line items at all') ||
    (message.includes('boq generation') && message.includes('/boq/generate')) ||
    message.includes('was never run') ||
    message.includes('none are computable') ||
    message.includes('not computable')
  );
};

/** True when group block BOQ flow requires extractData with intelligence_scope=GroupCommon. */
export const isGroupCommonExtractRequiredError = (err) => {
  const message = getApiErrorMessage(err, '').toLowerCase();
  if (!message) return false;

  return (
    message.includes('no block common-area drawing intelligence found') ||
    message.includes('no project common-area drawing intelligence found') ||
    (message.includes('extractdata') && message.includes('groupcommon')) ||
    (message.includes('extractdata') && message.includes('projectcommon')) ||
    (message.includes('intelligence_scope') && message.includes('groupcommon')) ||
    (message.includes('intelligence_scope') && message.includes('projectcommon'))
  );
};
